// Scherm 4 — de bronnenchat.
//
// Vorm uit V2_frontend.jsx r. 1638 (`ChatPopup`), het ontwerp dat de owner al
// had goedgekeurd: popup van 720×700 met radius 18, waas met blur, zwart
// kop-icoon, "Chat met knowledge base", een lege stand met voorbeeldvragen als
// volle regels, gebruikersbericht rechts in #111827 en antwoord links in
// #f3f4f6, invoerveld van 44px met een ronde verzendknop.
//
// Drie dingen zijn anders, alle drie omdat de data eronder anders is:
//
//  1. GEEN ＋ Skills-menu. V2 hangt daar vier agent-flows onder (Validator,
//     Adviseur, Trendvanger, Refurbisher). W4-besluit C1/C2 is "chat op de
//     bronnen, geen agent-flows" — een menu met vier ingangen die niets doen is
//     de badge-zonder-uitleg die W8 in de dummy aanwees.
//  2. DE VOORBEELDVRAGEN ZIJN HERSCHREVEN (W8 scherm 4: "voorbeeldvragen
//     herschrijven"). De dummy vroeg naar een lasbedrijf in Eindhoven, quick
//     wins in een portefeuille en subsidies voor Zorg. Er is één sector (ICT),
//     geen bedrijvenlaan en geen subsidielijn, dus drie van de vier waren
//     onbeantwoordbaar. Deze vier passen op de zes tools die de leeslaag
//     werkelijk heeft.
//  3. BRONNEN ONDER HET ANTWOORD. Het contract levert per citaat de titel, de
//     uitgever, het gezagscijfer en het letterlijke fragment; W8 noemt dit
//     scherm "bronnenchat mét citaat" en de owner vroeg bij het bedrijfsrapport
//     al om "alles doorklikbaar naar bronnen mét gezagscijfer = bewijslast".
//     Komt een antwoord zónder citaat, dan zegt het scherm dat — stil laten zou
//     de bewering zwaarder laten wegen dan hij is.
//
// SCHERMSESSIE 2026-08-25 — HET BONNETJE. De backend stuurde sinds
// chat-v2.1 veel meer mee dan dit scherm gebruikte: vervolgvragen,
// entity-verwijzingen, tellingen, het laag-label per bron en een stroom van
// toolstappen. De diepteanalyse (§5) vatte samen wat daarvan wél en niet op het
// scherm hoort: "toon het bonnetje, niet de gedachten". Dus geen redeneertekst
// (gemeten: voegt niets toe), maar wél de toolstappen als levende statusregel,
// een geteld-badge bij elk getal dat écht in de database is geteld, en de
// afslag "bekijk alle 43 op de Skills-tab" met het filter voorgezet — de
// gemeten oplossing voor het antwoord dat 8 van de 43 vaardigheden als de
// volledige lijst presenteerde. Die onderdelen staan in ChatBonnetje.tsx.
//
// De vier openingsvragen zijn niet langer vast maar volgen de rol, precies
// zoals V2 het tekende (ROLES r. 905-936: elke rol een eigen set, kop en
// placeholder). De teksten komen uit config/chat.yaml, waar elke vraag het
// eval-id draagt dat bewijst dat hij groen scoort — een openingsvraag is de
// handleiding van het systeem, dus hij mag niet stuklopen.

import { Sparkles } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type {
  ChatOpeningsvragen,
  ChatPropositie,
  ChatProposities,
  ChatRequest,
  ChatStap,
  KaartLanding,
} from "../contracts";
import { ApiError, api } from "../lib/api";
import { useMethodeV3 } from "../lib/meta";
import { ChatInspiratie } from "./ChatInspiratie";
import { color, font, radius, size, weight } from "./chatTokens";
import {
  TelZin,
  TrendlaagTelKaart,
  WaarDitOpRust,
  type Kader as TrendlaagKader,
  trendlaagWeging,
} from "../nieuw/trendlaag/Rust";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { vastePropositie } from "../lib/sectorkeuze";
import {
  RegelingenLijst,
  ReeksBlok,
  SkillLijst,
  TelKaart,
  TrendBlok,
  ZoekRegel,
  type Kaart,
  type PaneelDoel,
  type Reeks,
  type Zoekwerk,
} from "./ChatBlokken";
import {
  Afslagen,
  Toolstappen,
  Vervolgvragen,
  inlineTellingen,
  type Telling,
} from "./ChatBonnetje";
import { ChatPaneel } from "./ChatPaneel";
import { MARKER_BRON } from "./Verwijzing";
import {
  Bronnen,
  type Citation,
  type FundingBron,
  type Leescontext,
  tekstMetVerwijzingenEnGetallen,
} from "./ChatBronnen";

// De betaalmuurwachter (Betaalmuur.test.tsx) importeert de bronnenrij hiervandaan.
export { Bronnen };


/** Waar de browser de gesprekken bewaart, en hoeveel er blijven staan. */
const GESCHIEDENIS_SLEUTEL = "sof.chat.geschiedenis";
const GESCHIEDENIS_MAX = 20;

type BewaardGesprek = {
  id: string;
  titel: string;
  /** ISO-tijd van de laatste beurt. */
  tijd: string;
  berichten: Bericht[];
};

/**
 * De bewaarde gesprekken uit local storage.
 *
 * Alles is defensief: een kapotte of oude sleutel levert een lege lijst en geen
 * uitzondering. Local storage kan ook helemaal ontbreken (privacymodus, een
 * testomgeving), en dan werkt de chat gewoon zonder geschiedenis — dat is een
 * gemis, geen fout.
 */
function leesGeschiedenis(): BewaardGesprek[] {
  try {
    const rauw = window.localStorage.getItem(GESCHIEDENIS_SLEUTEL);
    if (!rauw) return [];
    const data = JSON.parse(rauw) as unknown;
    if (!Array.isArray(data)) return [];
    return data.filter(
      (g): g is BewaardGesprek =>
        typeof g === "object" &&
        g !== null &&
        typeof (g as BewaardGesprek).id === "string" &&
        Array.isArray((g as BewaardGesprek).berichten),
    );
  } catch {
    return [];
  }
}

function bewaarGesprek(id: string, berichten: Bericht[]): BewaardGesprek[] {
  const eerste = berichten.find((b) => b.rol === "user");
  if (!eerste) return leesGeschiedenis();
  const gesprek: BewaardGesprek = {
    id,
    titel: eerste.tekst.slice(0, 120),
    tijd: new Date().toISOString(),
    berichten,
  };
  const rest = leesGeschiedenis().filter((g) => g.id !== id);
  const alles = [gesprek, ...rest].slice(0, GESCHIEDENIS_MAX);
  try {
    window.localStorage.setItem(GESCHIEDENIS_SLEUTEL, JSON.stringify(alles));
  } catch {
    // Vol of geblokkeerd: het gesprek zelf werkt door.
  }
  return alles;
}

function wisGeschiedenis(): void {
  try {
    window.localStorage.removeItem(GESCHIEDENIS_SLEUTEL);
  } catch {
    // niets te wissen
  }
}

/** Eén gesprek uit de lijst halen (Chat 2.0, flow A: verwijderen per gesprek
 *  naast wis-alles). */
function wisGesprek(id: string): BewaardGesprek[] {
  const rest = leesGeschiedenis().filter((g) => g.id !== id);
  try {
    window.localStorage.setItem(GESCHIEDENIS_SLEUTEL, JSON.stringify(rest));
  } catch {
    // dan blijft de rij staan; erger is het niet
  }
  return rest;
}

/** De duim (W6) in de bewaarde geschiedenis zetten, bij het gesprek dat DIT
 *  antwoord draagt — gezocht op het server-id van het antwoord, niet op het
 *  gesprek dat nu toevallig open staat. Review ronde 1: wie na een duim meteen
 *  "Nieuw gesprek" koos, zag anders het oude gesprek leeg bewaard worden.
 *  Een duim is geen nieuwe beurt: volgorde en tijd in de lijst blijven staan. */
function zetDuimInGeschiedenis(
  gesprekId: string,
  duim: -1 | 1 | null,
  opmerking: string | null,
): BewaardGesprek[] {
  const alles = leesGeschiedenis();
  let geraakt = false;
  const draagt = (m: Bericht) => m.rol === "assistant" && m.gesprekId === gesprekId;
  const volgend = alles.map((g) => {
    if (!g.berichten.some(draagt)) return g;
    geraakt = true;
    return {
      ...g,
      berichten: g.berichten.map((m) => (draagt(m) ? { ...m, duim, opmerking } : m)),
    };
  });
  if (!geraakt) return alles;
  try {
    window.localStorage.setItem(GESCHIEDENIS_SLEUTEL, JSON.stringify(volgend));
  } catch {
    // Vol of geblokkeerd: de duim staat dan alleen in dit scherm, niet in de lijst.
  }
  return volgend;
}

function versGesprekId(): string {
  return `g-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/** Het patroon van `sessie` in het chatcontract. Het browser-id past er altijd
 *  in; de toets staat er zodat een ooit veranderd formaat een vraag niet met
 *  een 422 laat stranden — dan reist hij gewoon zonder sessie. */
const SESSIE_PATROON = /^[A-Za-z0-9_-]{1,64}$/;

type Bericht =
  | { rol: "user"; tekst: string }
  | {
      rol: "assistant";
      tekst: string;
      citaten: Citation[];
      tools: string[];
      stappen: ChatStap[];
      vervolg: string[];
      // B3 (feedbackronde 27-08): andere leesrichtingen bij een brede vraag,
      // als chips boven het antwoord. Niet-blokkerend.
      richtingen: string[];
      // B7: schreef de redactie in de jongste editie over dit onderwerp?
      editie: { kop: string; maand: string } | null;
      tellingen: Telling[];
      fundingBronnen: FundingBron[];
      // Chat 2.0: de blokken (kaartdata, reeksen) en de doorzocht-regel.
      kaarten: Kaart[];
      reeksen: Reeks[];
      zoekwerk: Zoekwerk | null;
      // Backlog 38: binnen welke propositie dit antwoord tot stand kwam.
      // Optioneel, zodat oudere bewaarde gesprekken gewoon blijven openen.
      propositie?: ChatPropositie | null;
      // Trendlaag ronde 3: het kader van dit antwoord (alleen bij aan-stand).
      // Optioneel, zodat oudere bewaarde gesprekken gewoon blijven openen.
      trendlaag?: TrendlaagKader | null;
      // W6: de rij in `gesprekken`, alleen als de server het gesprek bewaarde
      // (ingelogd). Zonder id geen duim — er is dan niets om hem aan te hangen.
      // Optioneel, zodat oudere bewaarde gesprekken gewoon blijven openen.
      gesprekId?: string;
      duim?: -1 | 1 | null;
      opmerking?: string | null;
    }
  | { rol: "fout"; tekst: string };

/**
 * DE PROPOSITIEREGEL (backlog 38). Eén zin boven elk antwoord: binnen welke
 * propositie de chat zocht. Uit het antwoord zelf (`propositie.label`), niet
 * uit de keuzelijst — wie halverwege wisselt ziet per antwoord wat er gold.
 * Staat de sector van de propositie nog niet in de kennisbank, dan valt de
 * regel op: dan komt het antwoord uit een andere sector.
 */
export function PropositieRegel({ propositie }: { propositie?: ChatPropositie | null }) {
  if (!propositie?.label) return null;
  const let_op = propositie.modus === "nog_niet_geladen";
  return (
    <div
      data-chat-propositieregel={propositie.modus}
      style={{
        fontSize: 11.5,
        color: let_op ? color.ink : color.faint,
        fontWeight: let_op ? weight.bold : undefined,
        margin: "0 0 6px",
      }}
    >
      {propositie.label}
    </div>
  );
}

/** De keuzelijst boven de chat: binnen welke propositie de volgende vraag
 *  gaat. `waarde` = een propositiesleutel of "alle". */
export function PropositieKeuze({
  proposities,
  waarde,
  onKies,
  breed = false,
}: {
  proposities: ChatProposities;
  waarde: string;
  onKies: (key: string) => void;
  /** Op telefoonbreedte staat de keuze op een eigen regel, over de hele breedte. */
  breed?: boolean;
}) {
  return (
    <select
      aria-label="Binnen welke propositie"
      title="Binnen welke propositie de chat zoekt"
      data-chat-propositie
      value={waarde}
      onChange={(e) => onKies(e.target.value)}
      style={{
        fontSize: 12,
        maxWidth: breed ? "100%" : 220,
        ...(breed ? { width: "100%", minWidth: 0 } : {}),
        padding: "4px 6px",
        borderRadius: radius.block,
        border: `1px solid ${color.disabled}`,
        background: color.surfaceAlt,
        color: color.ink,
        fontFamily: "inherit",
      }}
    >
      <option value="alle">Hele organisatie</option>
      {proposities.proposities.map((p) => (
        <option key={p.key} value={p.key}>
          {p.naam}
          {p.sectoren.length > 0 && p.geladen.length === 0 ? " (nog niet geladen)" : ""}
        </option>
      ))}
    </select>
  );
}

// TERUGVAL, niet de gewone stand. De openingsvragen komen uit
// /api/chat/openingsvragen (config/chat.yaml), waar elke vraag het eval-id
// draagt dat bewijst dat hij groen scoort. Faalt die route, dan blijven deze
// vier staan — een handleiding hoort niet te verdwijnen omdat een endpoint
// hikt. Ze zijn zelf ook op de tools gekozen (W8 scherm 4: de dummy vroeg naar
// een lasbedrijf, subsidies en een tweede sector, en drie van de vier waren
// daardoor onbeantwoordbaar).
const VOORBEELDVRAGEN = [
  "Welke vaardigheden vraagt de EU AI Act van mbo'ers?",
  "Wat zeggen de bronnen over het tekort aan cybersecurity-personeel?",
  "Welke trend raakt het werk van mbo'ers het hardst, en waar blijkt dat uit?",
  "Wat is er de laatste tijd veranderd?",
];

// Het model antwoordt in markdown — gemeten op de echte chat: "## Kernvaardigheden"
// en "**risicoclassificatie**" komen er gewoon uit. Rauw tonen zou dezelfde fout
// zijn als de {n}-markers uit sessie 1, waar de gebruiker letterlijk "{4}" las.
// Dit is bewust een MINIMALE renderer: kop, opsomming, vet en link. Alles wat hij
// niet kent blijft gewoon staan als tekst; hij verbergt nooit iets.
const VET_OF_LINK = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

function inlineTekst(regel: string, sleutel: string, ctx: Leescontext) {
  return regel.split(VET_OF_LINK).map((deel, i) => {
    if (deel.startsWith("**") && deel.endsWith("**")) {
      return (
        <b key={`${sleutel}-${i}`} style={{ fontWeight: weight.medium, color: color.ink }}>
          {tekstMetVerwijzingenEnGetallen(deel.slice(2, -2), `${sleutel}-${i}`, ctx)}
        </b>
      );
    }
    const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(deel);
    if (link) {
      return (
        <a
          key={`${sleutel}-${i}`}
          href={link[2]}
          target="_blank"
          rel="noreferrer"
          style={{ color: color.accentBlue, textDecoration: "none" }}
        >
          {link[1]}
        </a>
      );
    }
    return (
      <span key={`${sleutel}-${i}`}>
        {tekstMetVerwijzingenEnGetallen(deel, `${sleutel}-${i}`, ctx)}
      </span>
    );
  });
}

/** Een markdown-tabel in het spec-tabel-idioom, horizontaal scrollend. De
 *  renderer kende tot Chat 2.0 géén tabellen: het model schreef er wel eens
 *  één en de gebruiker las ruwe pipes — deel van "soms echt clunky" (P8). */
function Tabel({
  regels,
  sleutel,
  ctx,
}: {
  regels: string[];
  sleutel: string;
  ctx: Leescontext;
}) {
  const parse = (r: string) =>
    r.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());
  const inhoud = regels
    .map(parse)
    .filter((cellen) => !cellen.every((c) => c === "" || /^:?-+:?$/.test(c)));
  if (inhoud.length === 0) return null;
  const [kop, ...body] = inhoud;
  const cel = {
    padding: "6px 10px",
    textAlign: "left" as const,
    verticalAlign: "top" as const,
  };
  return (
    <div style={{ overflowX: "auto", margin: "8px 0" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, minWidth: 320 }}>
        <thead>
          <tr>
            {kop!.map((c, i) => (
              <th
                key={i}
                style={{
                  ...cel,
                  fontSize: 10.5,
                  fontWeight: weight.medium,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: color.faint,
                  borderBottom: `1px solid ${color.line}`,
                }}
              >
                {inlineTekst(c, `${sleutel}-k${i}`, ctx)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((rij, r) => (
            <tr key={r}>
              {rij.map((c, i) => (
                <td
                  key={i}
                  style={{ ...cel, borderBottom: `1px solid ${color.lineFaint}` }}
                >
                  {/* Het model schrijft in cellen soms letterlijk <br> als
                      regelscheiding (opname 27-08, frame 3:05); dat is geen
                      markdown en de renderer kent geen HTML — dus knippen we
                      de cel er zelf op en wordt elk stuk een eigen regel. */}
                  {c.split(/<br\s*\/?>/i).map((stuk, s, alle) => (
                    <div key={s} style={{ marginBottom: s < alle.length - 1 ? 3 : 0 }}>
                      {inlineTekst(stuk.trim(), `${sleutel}-r${r}c${i}s${s}`, ctx)}
                    </div>
                  ))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * De opmaak van het model op het scherm.
 *
 * CHAT 2.0 (ontwerp 26-08, §8): de typografieschaal is VERGRENDELD. Twee
 * kopniveaus en niets anders — H2 (##) 15px/700, H3 (###) 13,5px/700, dieper
 * dan ### wordt op ### afgevlakt. Eén bodymaat. Een regel die alleen vet is
 * blijft gewone tekst met vet, nooit een pseudo-kop. Dat is het antwoord op
 * opnamepunt P8 ("soms hele grote tekst, soms kleine — echt clunky"): niet
 * het model bepaalt de bladspiegel, de renderer doet dat.
 *
 * Nieuw: tabellen (zie Tabel), en het kopje "Conclusie" rendert als het vaste
 * slotblok — haarlijn erboven, label in kapitaaltjes, tekst in ink (P4).
 *
 * De filosofie blijft MINIMAAL: wat de renderer niet kent blijft gewoon staan
 * als tekst; hij verbergt nooit iets.
 *
 * Geëxporteerd voor de chatlogs van Beheer (W6 ronde 5): een bewaard antwoord
 * staat daar zoals de gebruiker het las, niet als ruwe markdown.
 */
export function Antwoordtekst({
  tekst,
  tellingen,
  bronnen,
  onBron,
  onPaneel,
  streaming = false,
  blokken,
  nummers,
}: {
  tekst: string;
  tellingen?: Telling[];
  bronnen?: Record<string, Citation>;
  onBron?: (documentId: string) => void;
  onPaneel?: (doel: PaneelDoel) => void;
  /** Tijdens het streamen krijgt de nieuwste regel een korte fade (P3). */
  streaming?: boolean;
  /** D3 (feedbackronde 27-08): een regel `[[blok:vaardigheden]]` of
   *  `[[blok:regelingen]]` rendert hiér het bijbehorende kaartenblok — de
   *  plek die het model aanwees. Zonder map (streamen) verdwijnt de regel;
   *  het blok verschijnt dan bij het afgeronde antwoord. */
  blokken?: Partial<Record<"vaardigheden" | "regelingen", ReactNode>>;
  /** Trendlaag (schakelaar aan): de nummers [n] als klikbare chipjes. */
  nummers?: Leescontext["nummers"];
}) {
  const ctx: Leescontext = {
    bronnen,
    onBron,
    onPaneel,
    tellingen: inlineTellingen(tellingen),
    gebruikt: new Set(),
    nummers,
    streaming,
  };
  const regels = tekst.split("\n");
  const uit: ReactNode[] = [];
  // Alles ná het kopje "Conclusie" kleurt ink: het slot draagt het antwoord.
  let inConclusie = false;
  // Alles ná "Blik in de toekomst" krijgt het gestippelde kader: speculatie
  // mag nooit op bewijs lijken (C3 — zelfde idioom als "afgeleide suggestie").
  let inToekomst = false;
  let i = 0;
  while (i < regels.length) {
    const laatsteRegel = streaming && i === regels.length - 1;
    const regel = laatsteRegel ? sluitVet(regels[i]!.trimEnd()) : regels[i]!.trimEnd();
    const key = String(i);
    const animatie = laatsteRegel ? "chat-regel-in" : undefined;
    if (!regel.trim()) {
      uit.push(<div key={key} style={{ height: 10 }} />);
      i += 1;
      continue;
    }
    if (/^-{3,}$/.test(regel.trim())) {
      i += 1;
      continue;
    }

    const blokMarker = /^\[\[blok:(vaardigheden|regelingen)\]\]$/.exec(regel.trim());
    if (blokMarker) {
      const naam = blokMarker[1] as "vaardigheden" | "regelingen";
      const blok = blokken?.[naam];
      if (blok) uit.push(<div key={key}>{blok}</div>);
      // Tijdens het streamen houdt een lege plek de ruimte vast: het blok
      // springt er straks in, niet eronder (testfeedback 03-11, rij 5).
      else if (streaming) uit.push(<div key={key} data-chat="blok-wacht" aria-hidden style={blokWachtStijl} />);
      i += 1;
      continue;
    }

    if (/^\s*\|.*\|\s*$/.test(regel)) {
      const blok: string[] = [];
      const start = i;
      while (i < regels.length && /^\s*\|.*\|\s*$/.test(regels[i]!.trimEnd())) {
        blok.push(regels[i]!.trimEnd());
        i += 1;
      }
      uit.push(<Tabel key={key} regels={blok} sleutel={`t${start}`} ctx={ctx} />);
      continue;
    }

    const kop = /^(#{1,4})\s+(.*)$/.exec(regel);
    if (kop) {
      const kopTekst = kop[2]!.trim();
      if (/^blik in de toekomst\b/i.test(kopTekst)) {
        inToekomst = true;
        inConclusie = false;
        uit.push(
          <div
            key={key}
            className={animatie}
            style={{
              marginTop: 16,
              marginBottom: 6,
              fontSize: 10.5,
              fontWeight: weight.bold,
              letterSpacing: "0.07em",
              textTransform: "uppercase",
              color: color.accentAmber,
            }}
          >
            ⤳ {kopTekst}
          </div>,
        );
        i += 1;
        continue;
      }
      inToekomst = false;
      if (/^conclusie\b/i.test(kopTekst)) {
        inConclusie = true;
        uit.push(
          <div
            key={key}
            className={animatie}
            style={{
              borderTop: `1px solid ${color.lineSoft}`,
              marginTop: 16,
              paddingTop: 12,
              marginBottom: 6,
              fontSize: 10.5,
              fontWeight: weight.bold,
              letterSpacing: "0.09em",
              textTransform: "uppercase",
              color: color.faint,
            }}
          >
            {kopTekst}
          </div>,
        );
        i += 1;
        continue;
      }
      inConclusie = false;
      const h2 = kop[1]!.length <= 2;
      uit.push(
        <div
          key={key}
          className={animatie}
          style={{
            fontWeight: weight.bold,
            color: color.ink,
            fontSize: h2 ? 15 : 13.5,
            letterSpacing: h2 ? "-0.01em" : undefined,
            marginTop: i === 0 ? 0 : h2 ? 18 : 14,
            marginBottom: h2 ? 8 : 6,
            lineHeight: 1.4,
          }}
        >
          {inlineTekst(kopTekst, key, ctx)}
        </div>,
      );
      i += 1;
      continue;
    }

    const genummerd = /^\s*(\d{1,2})[.)]\s+(.*)$/.exec(regel);
    if (genummerd) {
      uit.push(
        <div
          key={key}
          className={animatie}
          style={{ display: "flex", gap: 8, paddingLeft: 2, marginTop: 3 }}
        >
          <span
            style={{
              color: color.soft,
              fontWeight: weight.medium,
              minWidth: 16,
              flexShrink: 0,
            }}
          >
            {genummerd[1]}.
          </span>
          <span style={{ flex: 1 }}>{inlineTekst(genummerd[2]!, key, ctx)}</span>
        </div>,
      );
      i += 1;
      continue;
    }

    const bullet = /^\s*[-*•]\s+(.*)$/.exec(regel);
    if (bullet) {
      uit.push(
        <div
          key={key}
          className={animatie}
          style={{ display: "flex", gap: 8, paddingLeft: 2, marginTop: 3 }}
        >
          <span style={{ color: color.faint, flexShrink: 0 }}>•</span>
          <span style={{ flex: 1 }}>{inlineTekst(bullet[1]!, key, ctx)}</span>
        </div>,
      );
      i += 1;
      continue;
    }

    uit.push(
      <div
        key={key}
        className={animatie}
        style={
          inConclusie
            ? { color: color.ink }
            : inToekomst
              ? { borderLeft: "2px dashed #fde68a", paddingLeft: 10, marginTop: 3 }
              : undefined
        }
      >
        {inlineTekst(regel, key, ctx)}
      </div>,
    );
    i += 1;
  }
  return <>{uit}</>;
}

/**
 * Eén antwoord met zijn bonnetje, in de vaste anatomie van Chat 2.0 (§3 van
 * het ontwerp): doorzocht-regel → [telkaart] → tekst met inline
 * uitgeverspills → blokken (trendkaart/-lijstje, top-5-vaardigheden,
 * regelingen, grafiek) → bronnenrij (geciteerd / ook geraadpleegd) →
 * afslagen → vervolgvragen. Elke kaart en pill opent dezelfde slide-in.
 */
/** Smal scherm (telefoon): dan mag "Waar dit op rust" de volle breedte. */
function useSmal(): boolean {
  const vraag = "(max-width: 640px)";
  const [smal, setSmal] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(vraag).matches);
  useEffect(() => {
    const m = window.matchMedia?.(vraag);
    if (!m) return;
    const zet = () => setSmal(m.matches);
    m.addEventListener?.("change", zet);
    return () => m.removeEventListener?.("change", zet);
  }, []);
  return smal;
}

function Antwoordblok({
  bericht,
  onOpen,
  onPaneel,
  onVervolg,
  onDuim,
}: {
  bericht: Extract<Bericht, { rol: "assistant" }>;
  onOpen?: (landing: KaartLanding) => void;
  /** Opent een entiteit ín de chat (B5). */
  onPaneel: (doel: PaneelDoel) => void;
  onVervolg: (vraag: string) => void;
  /** Legt de bewaarde duim vast op het bericht zelf (W6). */
  onDuim?: (duim: -1 | 1 | null, opmerking: string | null) => void;
}) {
  const [openBron, setOpenBron] = useState<string | null>(null);
  // Trendlaag (CH-A): het nummer waarop in de tekst geklikt is; `keer` laat een
  // tweede klik op hetzelfde nummer opnieuw oplichten.
  const [actiefNr, setActiefNr] = useState<{ nr: number; keer: number } | null>(null);
  // documentId -> citatie, voor de inline pills mét layover (B4).
  const bronnen: Record<string, Citation> = {};
  for (const c of bericht.citaten) {
    bronnen[c.documentId] = c;
  }
  // Welke bronnen ook echt inline geciteerd zijn, in leesvolgorde: dat is de
  // splitsing van de bronnenrij (geciteerd / ook geraadpleegd) ÉN het getal
  // "in de tekst geciteerd" in de doorzocht-regel. Een marker met onbekend id
  // maar een titel die exact een opgehaalde bron is, telt mee — de renderer
  // maakt daar ook een pill van (zelfde terugval).
  const geciteerdIds: string[] = [];
  {
    const alleCitaten = bericht.citaten;
    const re = new RegExp(MARKER_BRON, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(bericht.tekst)) !== null) {
      if (m[1] !== "bron") continue;
      const id = m[2] ?? "";
      const titel = (m[3] ?? "").trim().toLowerCase();
      const c =
        bronnen[id] ??
        alleCitaten.find((k) => k.title && k.title.toLowerCase() === titel);
      if (c && !geciteerdIds.includes(c.documentId)) {
        geciteerdIds.push(c.documentId);
      }
    }
  }
  const skills = bericht.kaarten.filter((k) => k.soort === "skill");
  const trends = bericht.kaarten.filter((k) => k.soort === "trend");
  const laagAan = trendlaagAan();
  const smal = useSmal();
  // Trendlaag (CH-A): het kader van dit antwoord en de nummers die het kent —
  // alleen die worden in de tekst een klikbaar chipje.
  const laagRust = laagAan && bericht.trendlaag ? bericht.trendlaag : null;
  const laagNummers = new Set<number>(
    laagRust
      ? [
          ...laagRust.rust.trends.flatMap((t) => t.ontwikkelingen.map((o) => o.nr)),
          ...laagRust.rust.overwogen.map((o) => o.nr),
        ]
      : [],
  );
  // CH-A: een nummer in de tekst is één ontwikkeling; een klik opent haar kaart
  // (en haar regel in "Waar dit op rust" licht op voor als je terugkomt).
  const laagOntwikkeling = new Map<number, { id: string; titel: string }>(
    laagRust
      ? [
          ...laagRust.rust.trends.flatMap((t) => t.ontwikkelingen.map((o) => [o.nr, { id: o.id, titel: o.titel }] as const)),
          ...laagRust.rust.overwogen.map((o) => [o.nr, { id: o.id, titel: o.titel }] as const),
        ]
      : [],
  );
  const openOntwikkeling = (id: string, titel: string) => onPaneel({ soort: "ontwikkeling", id, titel });
  const fondsen = bericht.kaarten.filter((k) => k.soort === "funding");
  // Het telgetal woont in een kaart, nooit los in de tekst (owner 26-08). De
  // eerste count_matching wordt de telkaart en verliest zijn inline badge;
  // de rest houdt het oude gedrag. LIVE GEVANGEN 26-08: een telling die nul
  // opleverde werd een prominente kaart "0 · Bekijk alle 0 →" bovenaan het
  // antwoord — een hulpfilter van het model, geen hoofdantwoord. Nul-tellingen
  // blijven daarom gewoon tekst; de kaart is voor het getal dat het antwoord
  // draagt.
  const telHoofd =
    bericht.tellingen.find(
      (t) => t.tool === "count_matching" && t.aantal > 0,
    ) ?? null;
  const overigeTellingen = bericht.tellingen.filter((t) => t !== telHoofd);

  // D3: staat er een plaatsingsmarker in de tekst, dan rendert het blok DÁÁR
  // en niet nog eens onderaan — nooit dubbel, nooit weg.
  const skillBlok = <SkillLijst kaarten={skills} onOpenKaart={onPaneel} onOpen={onOpen} />;
  const regelingenBlok = <RegelingenLijst kaarten={fondsen} onOpenKaart={onPaneel} />;
  const skillsGeplaatst = /\[\[blok:vaardigheden\]\]/.test(bericht.tekst) && skills.length > 0;
  const regelingenGeplaatst = /\[\[blok:regelingen\]\]/.test(bericht.tekst) && fondsen.length > 0;

  // 4B: de platform-routes naast de vervolgvragen — afgeleid van wat dit
  // antwoord werkelijk aanraakte, nooit meer dan vier (geen tweede menu).
  const routes: { label: string; doe: () => void }[] = [];
  // Trendlaag (schakelaar aan): de route noemt en opent de eerste trend van
  // "Waar dit op rust", niet de eerste oude trend (bevinding).
  const laagTop = laagAan ? bericht.trendlaag?.rust?.trends?.[0] : undefined;
  const topTrend = laagTop?.bevindingen[0]
    ? { titel: laagTop.titel, id: laagTop.bevindingen[0] }
    : trends[0];
  if (topTrend) {
    routes.push({
      label: `Open "${topTrend.titel.length > 48 ? `${topTrend.titel.slice(0, 48)}…` : topTrend.titel}" in het detail`,
      doe: () => onPaneel({ soort: "trend", id: topTrend.id }),
    });
  }
  if (onOpen && (fondsen.length > 0 || bericht.fundingBronnen.length > 0)) {
    routes.push({
      label: "Alle regelingen op de Funding-tab",
      doe: () => onOpen({ soort: "tab", tab: "funding" }),
    });
  }
  if (onOpen && bericht.reeksen.length > 0) {
    routes.push({
      label: "Meer cijfers op de Arbeidsmarkt-tab",
      doe: () => onOpen({ soort: "tab", tab: "arbeidsmarkt" }),
    });
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <ZoekRegel
        zoekwerk={bericht.zoekwerk}
        stappen={bericht.stappen}
        geciteerd={geciteerdIds.length}
        opgehaald={bericht.citaten.length}
        laag={laagAan ? trendlaagWeging(bericht.trendlaag) : undefined}
      />
      <PropositieRegel propositie={bericht.propositie} />
      <Richtingen richtingen={bericht.richtingen} onKies={onVervolg} />
      <div
        style={{
          maxWidth: "82%",
          background: color.lineFaint,
          color: color.body,
          padding: "12px 16px",
          borderRadius: 14,
          fontSize: size.body,
          lineHeight: 1.65,
        }}
      >
        {/* Trendlaag (schakelaar aan): telde de chat op de trendlaag, dan is dát
            het getal van het antwoord ("N bevindingen uit N ontwikkelingen in N trends"). */}
        {laagAan && bericht.trendlaag?.tellingen?.[0] ? (
          <TrendlaagTelKaart telling={bericht.trendlaag.tellingen[0]} onOpen={onOpen} />
        ) : (
          telHoofd && <TelKaart telling={telHoofd} onOpen={onOpen} />
        )}
        {/* Trendlaag (CH-A): de eerste regel zegt waarop het antwoord rust, geteld. */}
        {laagRust && <TelZin kader={laagRust} />}
        <Antwoordtekst
          tekst={bericht.tekst}
          tellingen={overigeTellingen}
          bronnen={bronnen}
          onBron={setOpenBron}
          onPaneel={onPaneel}
          blokken={{
            ...(skillsGeplaatst ? { vaardigheden: skillBlok } : {}),
            ...(regelingenGeplaatst ? { regelingen: regelingenBlok } : {}),
          }}
          nummers={
            laagNummers.size > 0
              ? {
                  bekend: laagNummers,
                  onNummer: (nr) => {
                    setActiefNr((a) => ({ nr, keer: (a?.keer ?? 0) + 1 }));
                    const o = laagOntwikkeling.get(nr);
                    if (o) openOntwikkeling(o.id, o.titel);
                  },
                }
              : undefined
          }
        />
        {/* Uit-stand: het oude trendblok in de bel, zoals live. Aan-stand: "Waar
            dit op rust" staat ónder de bel (CH-A). */}
        {!(laagAan && (bericht.trendlaag || trends.length > 0)) && (
          <TrendBlok kaarten={trends} onOpenKaart={onPaneel} />
        )}
        {!skillsGeplaatst && skillBlok}
        {!regelingenGeplaatst && regelingenBlok}
        {bericht.reeksen.map((r) => (
          <ReeksBlok key={r.indicator} reeks={r} />
        ))}
      </div>
      {/* Trendlaag (schakelaar aan, editie 15: C14 = b, N14 = a): "Waar dit op
          rust" per trend in de vorm van het oude lijstblok, de Waarom nu-strip en
          het overwogene, ónder de antwoordbel; klik op een ontwikkeling = de
          ontwikkelingenkaart, "Open trend →" = de trendkaart (CH-A). */}
      {laagAan && (bericht.trendlaag || trends.length > 0) && (
        <div style={{ maxWidth: smal ? "100%" : "82%" }}>
          <WaarDitOpRust
            kader={bericht.trendlaag}
            bevindingIds={trends.map((k) => k.id)}
            onTrend={(id) => onPaneel({ soort: "trend", id })}
            onOntwikkeling={openOntwikkeling}
            actief={actiefNr}
          />
        </div>
      )}
      <div style={{ maxWidth: "82%" }}>
        <Bronnen
          citaten={bericht.citaten}
          tools={bericht.tools}
          // Als de regelingen al als lijstje in het antwoord staan zou de
          // pill-rij eronder hetzelfde twee keer zeggen.
          fundingBronnen={fondsen.length > 0 ? [] : bericht.fundingBronnen}
          geciteerdIds={geciteerdIds}
          onOpen={onOpen}
          onPaneel={onPaneel}
          openId={openBron}
          onOpenId={setOpenBron}
        />
        {bericht.editie && onOpen && (
          // B7: de twee insight-lagen kennen elkaar — schreef de redactie
          // deze maand over dit onderwerp, dan is dat één klik hiervandaan.
          <button
            onClick={() => onOpen({ soort: "tab", tab: "workspace" })}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              background: "none",
              border: "none",
              borderTop: `1px solid ${color.lineSoft}`,
              marginTop: 10,
              padding: "8px 2px 0",
              fontSize: 12,
              fontFamily: "inherit",
              color: color.soft,
              cursor: "pointer",
            }}
          >
            De redactie schreef hierover in de editie: "{bericht.editie.kop}" →
          </button>
        )}
        <Afslagen tellingen={overigeTellingen} onOpen={onOpen} />
        <Vervolgvragen vragen={bericht.vervolg} onKies={onVervolg} routes={routes} />
        {bericht.gesprekId && onDuim && (
          <DuimRegel
            gesprekId={bericht.gesprekId}
            duim={bericht.duim ?? null}
            opmerking={bericht.opmerking ?? null}
            onBewaard={onDuim}
          />
        )}
      </div>
    </div>
  );
}

/** Duim omhoog, in de lijnstijl van de andere iconen in de chat. */
function DuimIcoon({ omlaag, vol }: { omlaag?: boolean; vol: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={vol ? "currentColor" : "none"}
      fillOpacity={vol ? 0.18 : 0}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Eén tekening, gespiegeld voor omlaag: zo zijn de twee duimen gegarandeerd
      // elkaars spiegelbeeld.
      style={omlaag ? { transform: "rotate(180deg)" } : undefined}
      aria-hidden
    >
      <path d="M7 11v10H4a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1h3z" />
      <path d="M7 11l4-8a2.5 2.5 0 0 1 3 2.4V9h5.2a2 2 0 0 1 2 2.3l-1.3 8A2 2 0 0 1 17.9 21H7" />
    </svg>
  );
}

/**
 * De duim onder een antwoord (W6). Alleen zichtbaar als het antwoord een
 * `gesprekId` draagt: de server bewaart alleen gesprekken van een ingelogde
 * gebruiker, en een duim zonder rij om aan te hangen zou een knop zijn die
 * niets doet.
 *
 * Elke klik stuurt de VOLLEDIGE stand (duim + opmerking); de server zet beide
 * tegelijk. Dezelfde duim nog eens aanklikken haalt hem weg (null). Na duim
 * omlaag mag er één regel bij — optioneel, want een duim zonder uitleg is ook
 * een oordeel. Mislukt het bewaren, dan zegt de regel dat met de reden; stil
 * falen zou de gebruiker laten denken dat zijn oordeel binnen is.
 */
function DuimRegel({
  gesprekId,
  duim,
  opmerking,
  onBewaard,
}: {
  gesprekId: string;
  duim: -1 | 1 | null;
  opmerking: string | null;
  onBewaard: (duim: -1 | 1 | null, opmerking: string | null) => void;
}) {
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [tekst, setTekst] = useState("");

  async function bewaar(nieuw: -1 | 1 | null, nieuweOpmerking: string | null) {
    setBezig(true);
    setFout(null);
    try {
      const r = await api.duim(gesprekId, { duim: nieuw, opmerking: nieuweOpmerking });
      // De stand die de server teruggeeft is leidend, niet wat we stuurden.
      onBewaard(r.duim === 1 || r.duim === -1 ? r.duim : null, r.opmerking ?? null);
      setTekst("");
    } catch (err) {
      setFout(
        err instanceof ApiError
          ? `Je oordeel is niet bewaard: ${err.reason}`
          : "Je oordeel is niet bewaard.",
      );
    } finally {
      setBezig(false);
    }
  }

  const knop = (actief: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 26,
    height: 26,
    background: actief ? color.lineFaint : "none",
    border: `1px solid ${actief ? color.border : "transparent"}`,
    borderRadius: radius.input,
    color: actief ? color.ink : color.faint,
    cursor: bezig ? "default" : "pointer",
    padding: 0,
  });

  const opmerkingVersturen = () => void bewaar(-1, tekst.trim() || null);

  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        <button
          type="button"
          title="Goed antwoord"
          aria-label="Goed antwoord"
          aria-pressed={duim === 1}
          disabled={bezig}
          onClick={() => void bewaar(duim === 1 ? null : 1, null)}
          style={knop(duim === 1)}
        >
          <DuimIcoon vol={duim === 1} />
        </button>
        <button
          type="button"
          title="Niet goed"
          aria-label="Niet goed"
          aria-pressed={duim === -1}
          disabled={bezig}
          onClick={() => void bewaar(duim === -1 ? null : -1, null)}
          style={knop(duim === -1)}
        >
          <DuimIcoon omlaag vol={duim === -1} />
        </button>
        {fout && <span style={{ fontSize: 11.5, color: color.faint, marginLeft: 6 }}>{fout}</span>}
      </div>
      {duim === -1 &&
        (opmerking ? (
          <p style={{ fontSize: 11.5, color: color.faint, marginTop: 6, lineHeight: 1.5 }}>
            Je opmerking is bewaard: “{opmerking}”
          </p>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
            <input
              type="text"
              value={tekst}
              maxLength={2000}
              onChange={(e) => setTekst(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && tekst.trim() && !bezig) opmerkingVersturen();
              }}
              placeholder="Wat klopte er niet? (optioneel)"
              aria-label="Wat klopte er niet? (optioneel)"
              style={{
                flex: 1,
                minWidth: 0,
                padding: "6px 10px",
                fontSize: size.meta,
                fontFamily: "inherit",
                color: color.ink,
                background: color.surface,
                border: `1px solid ${color.border}`,
                borderRadius: radius.input,
                outline: "none",
              }}
            />
            <button
              type="button"
              title="Opmerking versturen"
              aria-label="Opmerking versturen"
              disabled={bezig || !tekst.trim()}
              onClick={opmerkingVersturen}
              style={{
                ...knop(false),
                color: tekst.trim() ? color.ink : color.disabled,
                cursor: bezig || !tekst.trim() ? "default" : "pointer",
              }}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                <path
                  d="M8 13V3M3.5 7.5L8 3l4.5 4.5"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ))}
    </div>
  );
}

/** B3: de andere leesrichtingen als chips boven het antwoord. Klik = die
 *  richting als nieuwe vraag stellen. Niet-blokkerend — het antwoord staat
 *  er al onder. */
function Richtingen({
  richtingen,
  onKies,
}: {
  richtingen: string[];
  onKies: (vraag: string) => void;
}) {
  if (richtingen.length === 0) return null;
  return (
    <div
      style={{
        maxWidth: "82%",
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: 6,
        margin: "0 0 8px",
      }}
    >
      <span style={{ fontSize: 11.5, color: color.faint }}>Ook een andere kant op:</span>
      {richtingen.map((r) => (
        <button
          key={r}
          onClick={() => onKies(r)}
          style={{
            border: `1px solid ${color.border}`,
            borderRadius: radius.pill,
            background: color.surface,
            padding: "3px 10px",
            fontSize: 12,
            color: color.body,
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {r}
        </button>
      ))}
    </div>
  );
}

/**
 * De onthulling van de antwoordtekst, PUUR AAN DE SCHERMKANT.
 *
 * De backend streamt bewust per REGEL en niet per token — die keuze staat
 * (ADR §9b): `parse_vervolgvragen` en `strip_entity_markers` werken op
 * complete regels. Deze hook toont hetzelfde, alleen geleidelijk; het
 * `klaar`-object blijft de waarheid.
 *
 * Herbouwd in de poetsronde 28-08, nadat een Playwright-tijdlijn de vier
 * haper-oorzaken had gemeten: een interval die bij élke tik werd herbouwd,
 * tempo in harde trappen, een bevriezing zodra de knip binnen een marker
 * viel, en een eindsprong van +1.882 tekens in één frame. Daarom nu:
 *  - één doorlopende frame-lus met een GLADDE snelheidscurve (doelSnelheid);
 *  - markers zijn GRATIS: de zichtbare tekst springt er atomair overheen
 *    (sprongOverMarker) in plaats van ervoor te bevriezen;
 *  - `klaar` laat de rest UITROLLEN (≤ ~1 s) in plaats van springen.
 */
/** Een vet stuk dat nog binnenkomt ("**Klantcon") staat al vet in beeld in
 *  plaats van met sterretjes; een kaal "**" aan het eind valt weg. */
export function sluitVet(regel: string): string {
  const n = (regel.match(/\*\*/g) ?? []).length;
  if (n % 2 === 0) return regel;
  if (regel.endsWith("**")) return regel.slice(0, -2);
  // Midden in het sluitteken ("**Klant*"): het halve sterretje valt weg.
  return `${regel.endsWith("*") ? regel.slice(0, -1) : regel}**`;
}

const blokWachtStijl = {
  height: 44,
  margin: "8px 0",
  borderRadius: 10,
  background: color.surfaceQuote,
  border: `1px dashed ${color.border}`,
} as const;

/** Zo lang wacht een eerste losse regel voor hij in beeld komt (rij 5). */
const TEKST_WACHT_MS = 700;

function useTekenReveal(tekst: string, klaar: boolean): string {
  const [tot, setTot] = useState(0);
  const pos = useRef(0);
  const tempo = useRef(0);
  const vorigeTijd = useRef<number | null>(null);

  useEffect(() => {
    // Nieuw antwoord (de tekst is korter dan wat we al toonden): opnieuw
    // beginnen. Anders zou de vorige stand als voorsprong blijven staan.
    if (tekst.length < tot) {
      setTot(0);
      pos.current = 0;
      tempo.current = 0;
    }
  }, [tekst, tot]);

  useEffect(() => {
    if (pos.current >= tekst.length) return;
    // jsdom kent requestAnimationFrame alleen in de visuele stand; de
    // terugval houdt de tests eerlijk zonder een tweede codeweg te worden.
    const vraagFrame: (cb: (nu: number) => void) => number =
      typeof window.requestAnimationFrame === "function"
        ? (cb) => window.requestAnimationFrame(cb)
        : (cb) => window.setTimeout(() => cb(performance.now()), 16);
    const stopFrame: (id: number) => void =
      typeof window.cancelAnimationFrame === "function"
        ? (id) => window.cancelAnimationFrame(id)
        : (id) => window.clearTimeout(id);

    let frame = 0;
    const stap = (nu: number) => {
      const dt = vorigeTijd.current === null ? 16 : Math.min(64, nu - vorigeTijd.current);
      vorigeTijd.current = nu;
      const doel = doelSnelheid(tekst.length - pos.current, klaar);
      // Exponentieel gladgestreken (~kwart seconde): het tempo verándert,
      // maar nooit als een zichtbare trede per binnengekomen regel. Bij
      // `klaar` telt alleen de uitrol, niet de aanloop ernaartoe.
      tempo.current = klaar
        ? doel
        : tempo.current + (doel - tempo.current) * Math.min(1, dt / 250);
      pos.current = Math.min(tekst.length, pos.current + (tempo.current * dt) / 1000);
      const zichtbaar = sprongOverMarker(tekst, Math.floor(pos.current));
      if (zichtbaar > pos.current) pos.current = zichtbaar;
      setTot((huidig) => (zichtbaar === huidig ? huidig : zichtbaar));
      if (pos.current < tekst.length) frame = vraagFrame(stap);
      else vorigeTijd.current = null;
    };
    frame = vraagFrame(stap);
    return () => {
      stopFrame(frame);
      vorigeTijd.current = null;
    };
  }, [tekst, klaar]);

  return tekst.slice(0, veiligeKnip(tekst, Math.min(tot, tekst.length)));
}

/**
 * Doelsnelheid in TEKENS per seconde, als vloeiende functie van de voorraad.
 *
 * De oude ladder (4→3→2→1→½ woord per tik op vaste tekengrenzen) was per
 * binnengekomen regel zichtbaar als versnellen-en-remmen. Dit is dezelfde
 * afweging — kruipen bij een bijna lege buffer zodat een modelpauze wordt
 * overbrugd, inhalen mét plafond bij een volle — maar zonder treden.
 * `klaar` vervangt de eindsprong: de rest rolt in hooguit ~een seconde uit,
 * frame voor frame, met een bodem zodat een klein restje vrijwel meteen
 * landt.
 */
export function doelSnelheid(achterstand: number, klaar: boolean): number {
  if (achterstand <= 0) return 0;
  const basis = 45 + 355 * Math.min(1, achterstand / 2400) ** 1.4;
  return klaar ? Math.max(basis, achterstand, 600) : basis;
}

/**
 * Een marker kost geen leestijd: valt de knip binnen een compleet
 * `[[…|…]]`-blok, dan springt de zichtbare positie er in één keer overheen —
 * de pill verschijnt atomair, precies zoals hij binnenkwam. Vóór deze sprong
 * BEVROOR de tekst aan het eind van vrijwel elke alinea (gemeten 28-08):
 * de knip wachtte vóór het haakje terwijl de leesklok doorliep, en alles wat
 * zich ophoopte popte er daarna in één keer in. Is de marker — tegen de
 * regel-garantie in — nog niet compleet, dan wacht de positie vóór het
 * haakje zonder zich op te hopen.
 */
export function sprongOverMarker(tekst: string, pos: number): number {
  const open = tekst.lastIndexOf("[[", pos - 1);
  if (open === -1) return pos;
  const sluit = tekst.indexOf("]]", open);
  if (sluit === -1) return Math.min(pos, open);
  if (pos >= open && pos < sluit + 2) return sluit + 2;
  return pos;
}

/**
 * Nooit midden in een `[[trend:…|titel]]`-marker afknippen.
 *
 * DIT IS GEEN NETHEID MAAR EEN HARDE REGEL van dit project. De backend streamt
 * bewust per REGEL zodat een marker altijd compleet is; een woord-voor-woord
 * onthulling zou die garantie weer breken en de gebruiker letterlijk `[[trend:`
 * laten lezen — dezelfde fout als de {n}-markers uit W1c sessie 1, waar er een
 * vitest tegen bestaat.
 *
 * De oplossing is de marker ATOMAIR te maken: staat er een `[[` open zonder
 * zijn `]]`, dan schuift de knip terug tot vóór dat haakje. De marker
 * verschijnt dan in één keer, zoals hij ook in één keer binnenkwam.
 */
export function veiligeKnip(tekst: string, tot: number): number {
  const open = tekst.lastIndexOf("[[", tot - 1);
  if (open === -1 || open >= tot) return tot;
  const sluit = tekst.indexOf("]]", open);
  // Sluit hij binnen het onthulde stuk, dan is de marker compleet.
  return sluit !== -1 && sluit + 2 <= tot ? tot : open;
}

/** Een kopbalk-icoon (owner 26-08, punt 2A: "maak van de buttons icoontjes").
 *  De naam blijft bestaan als tooltip én aria-label — een icoon zonder naam
 *  is een badge zonder uitleg. */
function IconKnop({
  titel,
  actief = false,
  onClick,
  children,
}: {
  titel: string;
  actief?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      title={titel}
      aria-label={titel}
      data-chat="icoonknop"
      data-actief={actief || undefined}
      style={{
        width: 30,
        height: 30,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: actief ? color.ink : color.lineFaint,
        color: actief ? color.surface : color.soft,
        border: "none",
        borderRadius: 8,
        cursor: "pointer",
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

const IKOON = {
  nieuw: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  ),
  geschiedenis: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  ),
  verklein: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M9.5 4.5h-5v5M14.5 19.5h5v-5M4.5 4.5l6 6M19.5 19.5l-6-6" />
    </svg>
  ),
  vergroot: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M14.5 4.5h5v5M9.5 19.5h-5v-5M19.5 4.5l-6 6M4.5 19.5l6-6" />
    </svg>
  ),
  sluit: (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M6 6l12 12M18 6L6 18" />
    </svg>
  ),
};

/** Eén keuzerij in de proefstand: label links, twee standen rechts. */
function ProefRij({
  label,
  waarde,
  opties,
  onKies,
}: {
  label: string;
  waarde: string;
  opties: [string, string][];
  onKies: (waarde: string) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: size.label, color: color.faint, fontWeight: weight.medium }}>
        {label}
      </span>
      <span style={{ display: "inline-flex", gap: 4 }}>
        {opties.map(([sleutel, tekst]) => (
          <button
            key={sleutel}
            onClick={() => onKies(sleutel)}
            aria-pressed={waarde === sleutel}
            style={{
              padding: "4px 10px",
              fontSize: 12,
              fontWeight: weight.medium,
              fontFamily: "inherit",
              borderRadius: radius.pill,
              border: `1px solid ${waarde === sleutel ? color.ink : color.border}`,
              background: waarde === sleutel ? color.ink : color.surface,
              color: waarde === sleutel ? color.surface : color.soft,
              cursor: "pointer",
            }}
          >
            {tekst}
          </button>
        ))}
      </span>
    </div>
  );
}

export function ChatPopup({
  open,
  onClose,
  initieleVraag = null,
  onVraagGebruikt,
  rol = null,
  onOpen,
  nieuweWeergave = false,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Een vraag die al klaarstaat in het invoerveld. Gebruikt door de doorklik
   * van een redactiekaart: de kaart "de vraag aan jou" landt in de chat MET
   * zijn vraag, anders is die kaart een uitdaging die je zelf moet overtypen.
   *
   * Bewust alleen VOORGELADEN en niet meteen verstuurd (owner-besluit B1,
   * 26-08): de gebruiker mag hem eerst bijstellen, en een chat die uit
   * zichzelf begint te praten is een verrassing waar niemand om vroeg. Wél
   * start hij een NIEUW gesprek — de vraag landde eerst onzichtbaar ónder een
   * oud transcript, en dat las als "de knop doet niets" (feed→chat-gat 2).
   */
  initieleVraag?: string | null;
  /**
   * Gemeld zodra de voorgeladen vraag in het invoerveld staat, zodat de
   * eigenaar hem kan wissen. Zonder dit bleef `chatVraag` in App plakken en
   * vulde de gewone chatknop een feed-vraag van tien minuten geleden opnieuw
   * in (feed→chat-gat 4).
   */
  onVraagGebruikt?: () => void;
  /**
   * De rol waarin de bezoeker kijkt, uit de rolwisselaar van de workspace
   * (V2-idioom: daar stuurt `roleKey` óók de chat aan). Bepaalt welke vier
   * openingsvragen, welke kop en welke placeholder je ziet. `null` = geen rol
   * gekozen; vandaag de normale stand, want de inlog bestaat nog niet.
   */
  rol?: string | null;
  /**
   * Doorklikken vanuit een antwoord: naar het detailpaneel van een trend,
   * vaardigheid of bron, of naar een tabblad met het filter voorgezet.
   *
   * BEWUST HETZELFDE TYPE als de redactiekaart gebruikt (`Landing` uit het
   * redactiecontract). Eén navigatiebus in het scherm; een tweede soort
   * doorklik met een eigen vorm zou dezelfde as twee keer beschrijven.
   */
  onOpen?: (landing: KaartLanding) => void;
  /** Nieuwe weergave: de stijl van de nieuwe weergave (nieuw/chat.css, via
   *  data-chat-attributen) en de regel "Meer inspiratie". Uit = de chat zoals
   *  hij live staat. */
  nieuweWeergave?: boolean;
}) {
  const methodeV3 = useMethodeV3();
  const [berichten, setBerichten] = useState<Bericht[]>([]);
  const [invoer, setInvoer] = useState("");
  const [bezig, setBezig] = useState(false);
  // DE PROEFSTAND (poetsronde 28-08). De losse Grondig-knop is weg: de owner
  // wil zelf kiezen wat we ná de 2×2-benchmark (model × diepte) aanbieden,
  // niet elke gebruiker die keuze geven. Tot dat besluit valt zit hier één
  // discrete proefschakelaar voor de vibe-check: model (Sonnet/MiniMax) en
  // diepte (standaard/grondig). Daarna verdwijnt hij.
  const [grondig, setGrondig] = useState(false);
  const [proefModel, setProefModel] = useState<"sonnet" | "minimax">("sonnet");
  const [proefOpen, setProefOpen] = useState(false);
  /**
   * Fullscreen is de STANDAARD (owner-opname rcgukmx6hc8l, 0:06). Een
   * antwoord met kopjes, bullets en een bonnetje eronder past niet in 720×700;
   * de referentie waar de owner het naast legde (Perplexity) gebruikt de hele
   * breedte. Verkleinen kan, want soms wil je het scherm eronder zien.
   */
  const [groot, setGroot] = useState(true);
  // Telefoonbreedte: de kop breekt dan in twee regels (zie de kop hieronder).
  const smalKop = useSmal();
  const [stappen, setStappen] = useState<ChatStap[]>([]);
  // De regels die al binnen zijn terwijl het antwoord nog geschreven wordt.
  const [levendeTekst, setLevendeTekst] = useState<string[]>([]);
  const [geschiedenis, setGeschiedenis] = useState(false);
  const [bewaard, setBewaard] = useState<BewaardGesprek[]>([]);
  // Eén id per gesprek: een tweede vraag werkt dezelfde rij bij in plaats van
  // een nieuwe aan te maken. Zonder dat staat elke beurt los in de lijst.
  const [gesprekId, setGesprekId] = useState<string>(versGesprekId);
  const [opening, setOpening] = useState<ChatOpeningsvragen | null>(null);
  // Backlog 38: de keuzelijst en wat de gebruiker koos. `null` = niets
  // gekozen; dan stuurt de chat geen propositie en geldt het profiel.
  const [proposities, setProposities] = useState<ChatProposities | null>(null);
  const [gekozenPropositie, setGekozenPropositie] = useState<string | null>(null);
  // De in-chat slide-in (B5): een kaart, lijstrij of bron-pill opent hier.
  const [paneel, setPaneel] = useState<PaneelDoel | null>(null);
  // De aanloopzin vervaagt in plaats van hard te verdwijnen (P3).
  const [vervaagt, setVervaagt] = useState(false);
  // Testfeedback 03-11 (rij 5): de eerste regel wacht even. Is het een
  // aanloopzin ("Ik zoek dit even op") en pakt het model daarna een tool, dan
  // komt hij nooit in beeld — geen wissel schrijven › onderzoek › schrijven.
  // Rijp = een tweede regel kwam binnen, of het bleef even stil.
  const [tekstRijp, setTekstRijp] = useState(false);
  const rijp = useRef(false);
  const rijpKlok = useRef<number | null>(null);
  // Het antwoord is binnen maar de onthulling rolt nog uit: `slotKlaar` zet
  // de reveal in de uitrolstand, `slotActie` doet daarná de wissel naar het
  // afgeronde bericht. Zonder deze twee verscheen de rest van de tekst in
  // één frame — de gemeten eindsprong (poetsronde 28-08).
  const [slotKlaar, setSlotKlaar] = useState(false);
  const slotActie = useRef<null | (() => void)>(null);
  const invoerRef = useRef<HTMLInputElement>(null);
  const eind = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  // Een geopend oud gesprek is LEZEN, geen bewerken: doorvragen maakt een
  // kopie met een vers id, zodat het origineel nooit stilzwijgend wordt
  // overschreven (flow A van het ontwerp — dat overschrijven was de gemeten
  // valkuil van de oude geschiedenis).
  const hersteld = useRef(false);
  const laatsteActiviteit = useRef(Date.now());
  const wasOpen = useRef(false);

  /**
   * HET NIEUWE GESPREK — dit codepad bestond niet (gemeten 26-08: geen enkele
   * plek riep ooit een reset aan; de chat heropende altijd met het vorige
   * gesprek en een hersteld gesprek werd bij elke vraag overschreven).
   */
  const nieuwGesprek = () => {
    setBerichten([]);
    setGesprekId(versGesprekId());
    setInvoer("");
    setStappen([]);
    setLevendeTekst([]);
    setPaneel(null);
    setGeschiedenis(false);
    hersteld.current = false;
  };

  useEffect(() => {
    if (open && initieleVraag) {
      // B1: de vraag wordt INGEVULD, niet verstuurd — maar wel in een vers,
      // leeg gesprek, zichtbaar en gefocust. Daarna meteen terugmelden zodat
      // de vraag niet blijft plakken voor de volgende keer.
      nieuwGesprek();
      setInvoer(initieleVraag);
      window.setTimeout(() => invoerRef.current?.focus(), 120);
      onVraagGebruikt?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initieleVraag]);

  useEffect(() => {
    // Heropenen ná een stilte van een uur begint leeg: het vorige gesprek
    // staat veilig in de geschiedenis, en een verse vraag hoort niet onder
    // een oud transcript te landen.
    if (open && !wasOpen.current) {
      const afgerond =
        berichten.length > 0 && berichten[berichten.length - 1]!.rol === "assistant";
      if (afgerond && !bezig && Date.now() - laatsteActiviteit.current > 3_600_000) {
        nieuwGesprek();
      }
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) setBewaard(leesGeschiedenis());
  }, [open]);

  useEffect(() => {
    // Eén keer ophalen zodra de chat voor het eerst opengaat. Faalt hij, dan
    // blijft `opening` null en toont het scherm de terugvalvragen — een
    // handleiding hoort niet te verdwijnen omdat een endpoint hikt.
    if (!open || opening) return;
    let leeft = true;
    api
      .chatOpeningsvragen()
      .then((o) => leeft && setOpening(o))
      .catch(() => undefined);
    return () => {
      leeft = false;
    };
  }, [open, opening]);

  useEffect(() => {
    // Eén keer ophalen; faalt hij, dan is er geen keuzelijst en kiest de
    // server zelf (profiel of hele organisatie).
    if (!open || proposities) return;
    let leeft = true;
    // Via Promise.resolve: ook een synchrone fout (een api zonder deze
    // functie, zoals oudere testmocks) wordt een afgewezen belofte.
    Promise.resolve()
      .then(() => api.chatProposities())
      .then((p) => leeft && setProposities(p))
      .catch(() => undefined);
    return () => {
      leeft = false;
    };
  }, [open, proposities]);

  // De rolset, met `algemeen` als terugval en de vier vaste vragen daaronder.
  const rolset =
    (rol ? opening?.rollen?.[rol] : undefined) ?? opening?.rollen?.["algemeen"];
  const openingsvragen = rolset?.vragen?.length
    ? rolset.vragen.map((v) => v.vraag)
    : VOORBEELDVRAGEN;
  const kopTitel = rolset?.titel || "Chat met de bronnen";
  const invoerHint =
    rolset?.placeholder || "Stel een vraag over trends, vaardigheden of bronnen…";

  // Spiegel van `paneel` voor de toetslistener: Escape sluit eerst de
  // slide-in en pas daarna de chat zelf.
  const paneelRef = useRef<PaneelDoel | null>(null);
  useEffect(() => {
    paneelRef.current = paneel;
  }, [paneel]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => invoerRef.current?.focus(), 100);
    const opToets = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (paneelRef.current) {
        setPaneel(null);
      } else {
        onClose();
      }
    };
    document.addEventListener("keydown", opToets);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("keydown", opToets);
    };
  }, [open, onClose]);

  // De onthulling. Staat hier bij de andere hooks en niet bij het renderen:
  // een hook mag nooit achter een vroege return staan.
  const onthuld = useTekenReveal(levendeTekst.join("\n"), slotKlaar);

  // De wissel naar het afgeronde bericht wacht tot de uitrol de gestreamde
  // tekst heeft ingehaald — dát maakt een einde aan de eindsprong. De
  // noodklok in `stuur` blijft de achtervang als de uitrol ooit blijft
  // hangen.
  useEffect(() => {
    if (slotKlaar && onthuld.length >= levendeTekst.join("\n").length) {
      slotActie.current?.();
    }
  }, [slotKlaar, onthuld, levendeTekst]);

  useEffect(() => {
    // Meescrollen is een nette-heid, geen functie: in een omgeving zonder
    // scrollIntoView (jsdom) mag het gesprek er niet op klappen.
    const el = eind.current;
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth" });
    }
  }, [berichten, bezig]);

  useEffect(() => {
    // Meescrollen mét de onthulling, maar alleen zolang de lezer al onderaan
    // stond — wie terugscrolt om iets na te lezen wordt niet teruggesleept.
    // (Gemeten 28-08: de tekst groeide onder de vouw door en de sprong kwam
    // pas toen het bericht landde.)
    const el = scroller.current;
    if (!el) return;
    const afstand = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (afstand > 0 && afstand < 160) el.scrollTop = el.scrollHeight;
  }, [onthuld, stappen]);

  if (!open) return null;

  const stuur = (tekst?: string) => {
    const vraag = (tekst ?? invoer).trim();
    if (!vraag || bezig) return;
    laatsteActiviteit.current = Date.now();
    // Doorvragen in een hersteld gesprek maakt een KOPIE met een vers id: het
    // origineel in de geschiedenis blijft zoals het was.
    let bewaarId = gesprekId;
    if (hersteld.current) {
      bewaarId = versGesprekId();
      setGesprekId(bewaarId);
      hersteld.current = false;
    }
    const heen: Bericht[] = [...berichten, { rol: "user", tekst: vraag }];
    setBerichten(heen);
    setInvoer("");
    setBezig(true);
    // Elke beurt begint met een schone statusregel: de stappen van het vorige
    // antwoord staan al onder dát antwoord.
    const gelopen: ChatStap[] = [];
    let regels: string[] = [];
    setStappen([]);
    setLevendeTekst([]);
    const zetRijp = (aan: boolean) => {
      if (rijpKlok.current !== null) window.clearTimeout(rijpKlok.current);
      rijpKlok.current = null;
      rijp.current = aan;
      setTekstRijp(aan);
    };
    zetRijp(false);
    // Stateless endpoint: de client draagt zijn eigen geschiedenis mee. Alleen
    // echte beurten gaan mee — een foutmelding is geen gespreksbeurt.
    // De cast is veilig: `heen` draagt altijd minstens de vraag die hierboven
    // is toegevoegd, en het contract eist alleen dat de lijst niet leeg is.
    const messages = heen
      .filter((b): b is Extract<Bericht, { rol: "user" | "assistant" }> => b.rol !== "fout")
      .map((b) => ({ role: b.rol, content: b.tekst })) as ChatRequest["messages"];
    api
      // De streamvariant levert hetzelfde antwoord, maar meldt onderweg wat hij
      // nakijkt. Valt de stroom weg, dan haalt hij het antwoord alsnog gewoon
      // op — dat zit in `chatStream` zelf.
      .chatStream(
        {
          messages,
          ...(grondig ? { diepte: "grondig" as const } : {}),
          // Alleen meesturen als hij afwijkt: afwezig = het configmodel.
          ...(proefModel !== "sonnet" ? { model: proefModel } : {}),
          // W6: het browser-id groepeert de vragen van één gesprek in de tabel
          // `gesprekken`. Hetzelfde id als in de geschiedenis — ook de kopie
          // van een hersteld gesprek, want dat is voor de server een nieuw gesprek.
          ...(SESSIE_PATROON.test(bewaarId) ? { sessie: bewaarId } : {}),
          // Backlog 38: alleen meesturen als de gebruiker zelf koos; anders
          // geldt de propositie uit zijn profiel (server).
          ...(gekozenPropositie ? { propositie: gekozenPropositie } : {}),
        },
        (stap) => {
          if (stap.soort === "tekst") {
            // Het antwoord komt regel voor regel binnen; elke regel is
            // compleet, dus er staat nooit een halve marker in beeld.
            regels = [...regels, stap.regel];
            setVervaagt(false);
            setLevendeTekst(regels);
            if (regels.length >= 2) zetRijp(true);
            else if (!rijp.current && rijpKlok.current === null) {
              rijpKlok.current = window.setTimeout(() => zetRijp(true), TEKST_WACHT_MS);
            }
            return;
          }
          if (stap.soort === "tool_start" && regels.length && !rijp.current) {
            // Nog niet in beeld geweest: stil weg, de werkkaart blijft staan.
            regels = [];
            zetRijp(false);
            setLevendeTekst([]);
          }
          if (stap.soort === "tool_start" && regels.length) {
            // DE AANLOOPZIN. Schrijft het model iets en pakt het dáárna
            // alsnog een tool, dan was die tekst geen antwoord maar een
            // aankondiging. Na de échte antwoordtekst komt per definitie
            // geen toolstap meer, dus wissen is altijd goed — maar hij
            // VERVAAGT (200 ms) in plaats van hard te verdwijnen: de sprong
            // was deel van "clunky" (P3).
            const oud = regels;
            regels = [];
            setVervaagt(true);
            window.setTimeout(() => {
              setVervaagt(false);
              setLevendeTekst((huidig) => (huidig === oud ? [] : huidig));
            }, 200);
          }
          gelopen.push(stap);
          setStappen([...gelopen]);
        },
      )
      .then((antwoord) => {
        laatsteActiviteit.current = Date.now();
        zetRijp(true);
        const wissel = () => {
          // Alles in één beweging: de streambubbel eruit, het afgeronde
          // bericht erin. Wie hier iets vergeet laat de gestreamde tekst een
          // frame lang onder zijn eigen afgeronde versie staan.
          setSlotKlaar(false);
          setLevendeTekst([]);
          setStappen([]);
          setBezig(false);
          setBerichten((b) => {
            const volgend: Bericht[] = [
              ...b,
              {
                rol: "assistant",
                tekst: antwoord.reply,
                citaten: antwoord.citations ?? [],
                tools: antwoord.toolsUsed ?? [],
                stappen: gelopen,
                vervolg: [...(antwoord.suggestedQuestions ?? [])],
                richtingen: [...(antwoord.richtingen ?? [])],
                editie: antwoord.editie ?? null,
                tellingen: [...(antwoord.tellingen ?? [])],
                fundingBronnen: [...(antwoord.fundingBronnen ?? [])],
                kaarten: [...(antwoord.kaarten ?? [])],
                reeksen: [...(antwoord.reeksen ?? [])],
                zoekwerk: antwoord.zoekwerk ?? null,
                propositie: antwoord.propositie ?? null,
                ...(antwoord.trendlaag ? { trendlaag: antwoord.trendlaag } : {}),
                // Afwezig = niet bewaard (geen inlog, of het wegschrijven
                // mislukte); dan staat er ook geen duim onder.
                ...(antwoord.gesprekId ? { gesprekId: antwoord.gesprekId } : {}),
              },
            ];
            // Pas bewaren als er een ANTWOORD ligt: een vraag zonder antwoord
            // terugzien in je geschiedenis is een half gesprek.
            setBewaard(bewaarGesprek(bewaarId, volgend));
            return volgend;
          });
        };
        if (regels.length === 0) {
          // Terugvalpad: de stroom brak vóór de eerste regel en er valt dus
          // niets uit te rollen — het antwoord verschijnt zoals voorheen in
          // één keer.
          wissel();
          return;
        }
        // Niet meteen wisselen: dat was de gemeten eindsprong (+1.882 tekens
        // in één frame). De uitrol maakt de onthulling eerst af; de noodklok
        // is de achtervang zodat een gesprek nooit aan de reveal blijft
        // hangen.
        const nood = window.setTimeout(() => slotActie.current?.(), 2500);
        slotActie.current = () => {
          window.clearTimeout(nood);
          slotActie.current = null;
          wissel();
        };
        setSlotKlaar(true);
      })
      .catch((err: unknown) => {
        slotActie.current = null;
        setSlotKlaar(false);
        setBezig(false);
        setStappen([]);
        setLevendeTekst([]);
        setBerichten((b) => [
          ...b,
          {
            rol: "fout",
            tekst:
              err instanceof ApiError
                ? err.status === 503
                  ? "De chat staat uit op de server: er is geen modelsleutel ingesteld."
                  : err.reason
                : "Er ging iets mis bij het beantwoorden.",
          },
        ]);
      });
  };

  const leeg = berichten.length === 0;

  // De duim leeft op het bericht zelf en reist dus mee in de geschiedenis: wie
  // een oud gesprek heropent ziet zijn eigen oordeel terug.
  // Op het server-id van het antwoord, niet op een index: tussen de klik en het
  // antwoord van de server kan een ander gesprek open staan.
  const zetDuim = (antwoordId: string, duim: -1 | 1 | null, opmerking: string | null) => {
    setBerichten((b) =>
      b.map((m) =>
        m.rol === "assistant" && m.gesprekId === antwoordId ? { ...m, duim, opmerking } : m,
      ),
    );
    setBewaard(zetDuimInGeschiedenis(antwoordId, duim, opmerking));
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(3px)",
        fontFamily: font,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-chat="venster"
        data-chat-stijl={nieuweWeergave ? "nieuw" : undefined}
        style={{
          background: color.surface,
          borderRadius: groot ? 14 : 18,
          width: groot ? "min(1360px,96vw)" : "min(720px,94vw)",
          height: groot ? "94vh" : "min(700px,88vh)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
          display: "flex",
          flexDirection: "column",
          // De in-chat slide-in (B5) leeft bínnen dit venster: eigen laag,
          // dus geen gevecht met de z-index van het gedeelde SlidePanel.
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Op telefoonbreedte (375 px) liep de kop 492 px breed en vielen de
            pictogramknoppen rechts buiten beeld. Smal: titel en knoppen op de
            eerste regel (knoppen altijd zichtbaar, het logo valt weg zodat de
            titel heel blijft), de propositiekeuze op een eigen regel eronder.
            Breed: precies zoals het was. */}
        <div
          data-chat="kop"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: smalKop ? "12px 16px" : "16px 24px",
            borderBottom: `1px solid ${color.lineSoft}`,
            ...(smalKop ? { flexWrap: "wrap" as const, rowGap: 10, columnGap: 8 } : {}),
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              ...(smalKop ? { flex: "1 1 0", minWidth: 0 } : {}),
            }}
          >
            <div
              data-chat="logo"
              style={{
                width: 28,
                height: 28,
                borderRadius: 8,
                background: color.ink,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                ...(smalKop ? { display: "none" } : {}),
              }}
            >
              {nieuweWeergave ? (
                <Sparkles size={15} strokeWidth={1.7} aria-hidden />
              ) : (
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v12M2 8h12" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              )}
            </div>
            <div style={smalKop ? { minWidth: 0 } : undefined}>
              <div
                style={{
                  fontSize: 15.5,
                  fontWeight: weight.bold,
                  color: color.ink,
                  lineHeight: 1.1,
                  ...(smalKop ? { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const } : {}),
                }}
              >
                {kopTitel}
              </div>
              <div style={{ fontSize: 11.5, color: color.faint }}>
                {/* Methode v3: wie het zegt in plaats van het gezagscijfer. */}
                Antwoorden komen uit het corpus,{" "}
                {methodeV3 ? "met citaat en wie het zegt" : "met citaat en gezagscijfer"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {proposities && !smalKop && !vastePropositie() && (
              <PropositieKeuze
                proposities={proposities}
                waarde={gekozenPropositie ?? proposities.standaard ?? "alle"}
                onKies={setGekozenPropositie}
              />
            )}
            {/* Iconen i.p.v. tekstknoppen (owner 26-08, 2A); elke naam blijft
                als tooltip en aria-label bestaan. */}
            <IconKnop titel="Nieuw gesprek" onClick={nieuwGesprek}>
              {IKOON.nieuw}
            </IconKnop>
            <IconKnop
              titel="Geschiedenis"
              actief={geschiedenis}
              onClick={() => setGeschiedenis(!geschiedenis)}
            >
              {IKOON.geschiedenis}
            </IconKnop>
            <IconKnop
              titel={groot ? "Verkleinen" : "Op volle breedte"}
              onClick={() => setGroot((g) => !g)}
            >
              {groot ? IKOON.verklein : IKOON.vergroot}
            </IconKnop>
            <IconKnop titel="Sluiten" onClick={onClose}>
              {IKOON.sluit}
            </IconKnop>
          </div>
          {proposities && smalKop && !vastePropositie() && (
            <div style={{ flexBasis: "100%", minWidth: 0 }}>
              <PropositieKeuze
                proposities={proposities}
                waarde={gekozenPropositie ?? proposities.standaard ?? "alle"}
                onKies={setGekozenPropositie}
                breed
              />
            </div>
          )}
        </div>

        <div ref={scroller} data-chat-berichten style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>
          {geschiedenis ? (
            <div style={{ maxWidth: 560, margin: "0 auto", paddingTop: 8 }}>
              <p
                style={{
                  fontSize: size.meta,
                  color: color.faint,
                  lineHeight: 1.6,
                  marginTop: 0,
                }}
              >
                Je gesprekken worden in deze browser bewaard, niet op de server: de
                chat is bewust stateless en er is nog geen inlog om ze aan te hangen.
                Ze staan dus alleen op dit apparaat, en wissen doe je hieronder.
              </p>
              <button
                onClick={nieuwGesprek}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  border: `1px dashed ${color.disabled}`,
                  borderRadius: radius.block,
                  background: color.surfaceAlt,
                  padding: "11px 14px",
                  font: "inherit",
                  fontSize: size.meta,
                  fontWeight: weight.medium,
                  color: color.ink,
                  cursor: "pointer",
                  marginTop: 4,
                }}
              >
                ＋ Begin een nieuw gesprek
              </button>
              {bewaard.length === 0 ? (
                <div
                  style={{
                    fontSize: size.body,
                    color: color.soft,
                    textAlign: "center",
                    paddingTop: 32,
                  }}
                >
                  Nog geen eerdere gesprekken.
                </div>
              ) : (
                <>
                  <div style={{ display: "grid", gap: 8, marginTop: 14 }}>
                    {bewaard.map((g) => (
                      <div
                        key={g.id}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          background: color.surface,
                          border: `1px solid ${color.border}`,
                          borderRadius: radius.input,
                          padding: "11px 14px",
                        }}
                      >
                        <button
                          onClick={() => {
                            // Een oud gesprek openen is LEZEN: doorvragen
                            // maakt straks een kopie (zie stuur), het
                            // origineel blijft staan. Oudere bewaarde
                            // gesprekken missen velden van latere rondes
                            // (kaarten, richtingen, ...): aanvullen, zodat
                            // het scherm er nooit op omvalt.
                            setBerichten(g.berichten.map((b) =>
                              b.rol === "assistant"
                                ? {
                                    ...b,
                                    citaten: b.citaten ?? [],
                                    tools: b.tools ?? [],
                                    stappen: b.stappen ?? [],
                                    vervolg: b.vervolg ?? [],
                                    richtingen: b.richtingen ?? [],
                                    editie: b.editie ?? null,
                                    tellingen: b.tellingen ?? [],
                                    fundingBronnen: b.fundingBronnen ?? [],
                                    kaarten: b.kaarten ?? [],
                                    reeksen: b.reeksen ?? [],
                                    zoekwerk: b.zoekwerk ?? null,
                                  }
                                : b,
                            ));
                            setGesprekId(g.id);
                            hersteld.current = true;
                            laatsteActiviteit.current = Date.now();
                            setGeschiedenis(false);
                          }}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            textAlign: "left",
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            font: "inherit",
                          }}
                        >
                          <div style={{ fontSize: size.meta, color: color.ink, lineHeight: 1.45 }}>
                            {g.titel}
                          </div>
                          <div style={{ fontSize: 11.5, color: color.faint, marginTop: 3 }}>
                            {new Date(g.tijd).toLocaleString("nl-NL", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                            {` · ${g.berichten.filter((b) => b.rol === "user").length} vragen`}
                          </div>
                        </button>
                        <button
                          onClick={() => setBewaard(wisGesprek(g.id))}
                          title="Dit gesprek verwijderen"
                          aria-label={`Gesprek verwijderen: ${g.titel}`}
                          style={{
                            background: "none",
                            border: "none",
                            padding: "2px 4px",
                            fontSize: 13,
                            color: color.disabled,
                            cursor: "pointer",
                            flexShrink: 0,
                            font: "inherit",
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      wisGeschiedenis();
                      setBewaard([]);
                    }}
                    style={{
                      marginTop: 14,
                      background: "none",
                      border: "none",
                      padding: 0,
                      font: "inherit",
                      fontSize: size.meta,
                      color: color.faint,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    wis alle bewaarde gesprekken
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {leeg && (
                <div
                  style={{
                    // Nieuwe weergave: minHeight i.p.v. height, zodat een opengeklapte
                    // "Meer inspiratie" naar onder groeit en de bovenkant bereikbaar blijft
                    // (met height + centreren viel hij boven buiten beeld).
                    ...(nieuweWeergave ? { minHeight: "100%" } : { height: "100%" }),
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    maxWidth: 560,
                    margin: "0 auto",
                  }}
                >
                  <div style={{ textAlign: "center", marginBottom: 28 }}>
                    <div
                      data-chat="leeg-icoon"
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: 14,
                        background: color.ink,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 14px",
                      }}
                    >
                      {nieuweWeergave ? (
                        <Sparkles size={22} strokeWidth={1.7} aria-hidden />
                      ) : (
                        <svg width="22" height="22" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M8 2v12M2 8h12"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: weight.bold,
                        color: color.ink,
                        marginBottom: 6,
                      }}
                    >
                      Waar kan ik je mee helpen?
                    </div>
                    <div style={{ fontSize: size.meta, color: color.faint, lineHeight: 1.55 }}>
                      Ik zoek in de trends, de vaardigheden, de arbeidsmarktcijfers,
                      de subsidieregelingen en de brondocumenten van dit dashboard.
                      Over opleidingsaanbod en bedrijven weet ik niets — die lanen
                      bestaan nog niet.
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {openingsvragen.map((q) => (
                      <button
                        key={q}
                        data-chat="opening"
                        onClick={() => stuur(q)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 11,
                          background: color.surface,
                          border: `1px solid ${color.line}`,
                          borderRadius: radius.card,
                          padding: "13px 16px",
                          cursor: "pointer",
                          textAlign: "left",
                          fontFamily: "inherit",
                          transition: "all 0.12s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = color.ink;
                          e.currentTarget.style.background = color.surfaceAlt;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = color.line;
                          e.currentTarget.style.background = color.surface;
                        }}
                      >
                        <span style={{ fontSize: size.body, color: color.disabled }}>›</span>
                        <span style={{ fontSize: 13.5, color: color.body, flex: 1 }}>{q}</span>
                      </button>
                    ))}
                    {nieuweWeergave && <ChatInspiratie onVraag={stuur} />}
                  </div>
                </div>
              )}

              {berichten.map((m, i) =>
                m.rol === "user" ? (
                  <div
                    key={i}
                    style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}
                  >
                    <div
                      data-chat="vraag"
                      style={{
                        maxWidth: "82%",
                        background: color.ink,
                        color: color.surface,
                        padding: "12px 16px",
                        borderRadius: 14,
                        fontSize: size.body,
                        lineHeight: 1.65,
                        whiteSpace: "pre-line",
                      }}
                    >
                      {m.tekst}
                    </div>
                  </div>
                ) : m.rol === "fout" ? (
                  <div key={i} style={{ marginBottom: 12 }}>
                    <div
                      style={{
                        maxWidth: "82%",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        color: color.accentRed,
                        padding: "12px 16px",
                        borderRadius: 14,
                        fontSize: size.meta,
                        lineHeight: 1.6,
                      }}
                    >
                      {m.tekst}
                    </div>
                  </div>
                ) : (
                  <Antwoordblok
                    key={i}
                    bericht={m}
                    onOpen={onOpen}
                    onPaneel={setPaneel}
                    onVervolg={stuur}
                    onDuim={(duim, opmerking) => {
                      if (m.gesprekId) zetDuim(m.gesprekId, duim, opmerking);
                    }}
                  />
                ),
              )}

              {bezig && (
                <div style={{ marginBottom: 12 }}>
                  {/* Zodra de tekst begint te stromen klapt de stappenlijst in:
                      hij heeft dan zijn werk gedaan en hoort het beeld niet
                      meer te dragen. Verdwijnen mag hij niet - hij is de
                      verantwoording. Zolang er GEEN tekst is draagt een
                      werkkaart het beeld (D4, feedbackronde 27-08): fullscreen
                      met alleen een lijstje linksboven las als kapot. */}
                  {!(levendeTekst.length > 0 && tekstRijp) ? (
                    <div
                      style={{
                        maxWidth: "82%",
                        border: `1px solid ${color.lineSoft}`,
                        borderRadius: radius.block,
                        background: color.surfaceAlt,
                        padding: "10px 16px 8px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10.5,
                          fontWeight: weight.medium,
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: color.faint,
                          marginBottom: 2,
                        }}
                      >
                        Onderzoek loopt
                      </div>
                      <Toolstappen stappen={stappen} bezig />
                    </div>
                  ) : (
                    <Toolstappen stappen={stappen} bezig={false} ingeklapt />
                  )}
                  {levendeTekst.length > 0 && tekstRijp && (
                    <div
                      className={vervaagt ? "chat-vervaagt" : undefined}
                      style={{
                        maxWidth: "82%",
                        background: color.lineFaint,
                        color: color.body,
                        padding: "12px 16px",
                        borderRadius: 14,
                        fontSize: size.body,
                        lineHeight: 1.65,
                      }}
                    >
                      {/* Dezelfde renderer als het afgeronde antwoord, dus de
                          opmaak springt niet zodra het klaar is. De
                          geteld-badges en blokken komen pas met het
                          slotantwoord: een badge kan niet bestaan vóór de
                          telling binnen is. */}
                      <Antwoordtekst tekst={onthuld} streaming />
                    </div>
                  )}
                </div>
              )}
              <div ref={eind} />
            </>
          )}
        </div>

        {!geschiedenis && (
          <div
            style={{
              // Telefoonbreedte: smallere rand, zodat de verstuurknop in beeld blijft.
              padding: smalKop ? "12px 16px 14px" : "14px 24px 18px",
              borderTop: `1px solid ${color.lineSoft}`,
            }}
          >
            <div style={{ display: "flex", gap: smalKop ? 8 : 10, alignItems: "center" }}>
              <input
                ref={invoerRef}
                data-chat="invoer"
                value={invoer}
                onChange={(e) => setInvoer(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && stuur()}
                placeholder={invoerHint}
                style={{
                  flex: 1,
                  // Een invoerveld heeft een eigen minimumbreedte; zonder dit
                  // duwde het op 375 px de verstuurknop buiten het venster.
                  minWidth: 0,
                  height: 44,
                  border: `1.5px solid ${color.border}`,
                  borderRadius: radius.card,
                  padding: "0 16px",
                  fontSize: size.body,
                  fontFamily: "inherit",
                  outline: "none",
                  color: color.ink,
                }}
              />
              {/* DE PROEFSTAND (poetsronde 28-08). De losse Grondig-knop is
                  weg — de owner kiest ná de 2×2-benchmark zelf wat we
                  gebruikers aanbieden. Tot dat besluit: één discrete
                  schakelaar voor de vibe-check, model × diepte. */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button
                  onClick={() => setProefOpen(!proefOpen)}
                  data-chat="proef"
                  aria-expanded={proefOpen}
                  title="Proefstand: vergelijk model en zoekdiepte. Tijdelijk, tot het benchmarkbesluit."
                  style={{
                    height: 44,
                    padding: "0 14px",
                    background:
                      proefModel === "sonnet" && !grondig ? color.surface : color.ink,
                    border: `1.5px solid ${
                      proefModel === "sonnet" && !grondig ? color.border : color.ink
                    }`,
                    color: proefModel === "sonnet" && !grondig ? color.soft : color.surface,
                    borderRadius: radius.card,
                    fontSize: 12.5,
                    fontWeight: weight.medium,
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {proefModel === "sonnet" && !grondig
                    ? "proef"
                    : ["proef:", proefModel !== "sonnet" ? "MiniMax" : null,
                       grondig ? "grondig" : null]
                        .filter(Boolean)
                        .join(" ")}
                </button>
                {proefOpen && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 52,
                      right: 0,
                      width: 300,
                      background: color.surface,
                      border: `1px solid ${color.border}`,
                      borderRadius: radius.card,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      padding: "14px 16px",
                      zIndex: 15,
                      textAlign: "left",
                    }}
                  >
                    <ProefRij
                      label="Model"
                      waarde={proefModel}
                      opties={[
                        ["sonnet", "Sonnet"],
                        ["minimax", "MiniMax"],
                      ]}
                      onKies={(v) => setProefModel(v as "sonnet" | "minimax")}
                    />
                    <ProefRij
                      label="Diepte"
                      waarde={grondig ? "grondig" : "standaard"}
                      opties={[
                        ["standaard", "Standaard"],
                        ["grondig", "Grondig"],
                      ]}
                      onKies={(v) => setGrondig(v === "grondig")}
                    />
                    <div
                      style={{
                        fontSize: size.label,
                        color: color.faint,
                        lineHeight: 1.5,
                        marginTop: 10,
                      }}
                    >
                      Proefstand voor de vergelijking. De 2×2-benchmark (model ×
                      diepte, mét prijs en snelheid) draagt straks het besluit;
                      daarna verdwijnt deze knop. Grondig duurt langer en kost
                      2-3× de vraagprijs.
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={() => stuur()}
                data-chat="stuur"
                disabled={bezig}
                aria-label="Vraag versturen"
                style={{
                  width: 44,
                  height: 44,
                  flexShrink: 0,
                  background: bezig ? color.disabled : color.ink,
                  border: "none",
                  color: color.surface,
                  borderRadius: radius.card,
                  fontSize: 17,
                  fontWeight: weight.medium,
                  fontFamily: "inherit",
                  cursor: bezig ? "default" : "pointer",
                }}
              >
                →
              </button>
            </div>
          </div>
        )}

        {/* De in-chat slide-in (B5): detail opent bínnen het venster, met
            "← Terug naar het antwoord" — het gesprek blijft staan. */}
        <ChatPaneel doel={paneel} onClose={() => setPaneel(null)} />
      </div>
    </div>
  );
}
